<template>
  <DataView 
    class="custom-table" 
    v-bind="tableConfig"
    @page="handlePageChange"
  >
    <!-- Slots para vista de tabla -->
    <template
      v-for="col in columns"
      :key="col.field"
      v-slot:[`table__${col.field}__body`]="{ data }"
    >
      <div v-if="isBooleanString(data[col.field])" class="flex">
        <i
          v-if="data[col.field] === true || data[col.field] === 'True'
          || data[col.field] === 'true'"
          class="pi pi-check-circle text-green-500"
          @mouseenter="!isTouchDevice() && showTooltip($event, { alwaysShow: true, text: 'Sí' })"
          @mouseleave="!isTouchDevice() && hideTooltip()"
          @click="isTouchDevice() && 
          showTooltip($event, { alwaysShow: true, text: 'Sí', toggle: true })"
        ></i>
        <i
          v-else
          class="pi pi-times-circle text-red-500"
          @mouseenter="!isTouchDevice() && showTooltip($event, { alwaysShow: true, text: 'No' })"
          @mouseleave="!isTouchDevice() && hideTooltip()"
          @click="isTouchDevice() &&
          showTooltip($event, { alwaysShow: true, text: 'No', toggle: true })"
        ></i>
      </div>  
      <div
        v-else-if="data[col.field] === null ||
        data[col.field] === undefined || data[col.field] === ''"
      >
        <span class="boolean-empty">—</span>
      </div>
      <div 
        class="long-text-column" 
        v-else 
          @mouseenter="!isTouchDevice() && showTooltip($event, { text: data[col.field] })"
          @mouseleave="!isTouchDevice() && hideTooltip()"
          @click="isTouchDevice() &&
          showTooltip($event, { alwaysShow: false, text: data[col.field], toggle: true })"
      >
        {{ data[col.field] }}
      </div>
    </template>

    <!-- Slots para vista de lista -->
    <template
      v-for="col in columns"
      :key="`list-${col.field}`"
      v-slot:[`list__${col.field}`]="{ data }"
    >
      <div v-if="isBooleanString(data[col.field])" class="flex">
        <i
          v-if="data[col.field] === true || data[col.field] === 'True'
          || data[col.field] === 'true'"
          class="pi pi-check-circle text-green-500"
          @mouseenter="!isTouchDevice() && showTooltip($event, { alwaysShow: true, text: 'Sí' })"
          @mouseleave="!isTouchDevice() && hideTooltip()"
          @click="isTouchDevice() &&
          showTooltip($event, { alwaysShow: true, text: 'Sí', toggle: true })"
        ></i>
        <i
          v-else
          class="pi pi-times-circle text-red-500"
          @mouseenter="!isTouchDevice() && showTooltip($event, { alwaysShow: true, text: 'No' })"
          @mouseleave="!isTouchDevice() && hideTooltip()"
          @click="isTouchDevice() &&
          showTooltip($event, { alwaysShow: true, text: 'No', toggle: true })"
        ></i>
      </div>  
      <div
        v-else-if="data[col.field] === null ||
        data[col.field] === undefined || data[col.field] === ''"
      >
        <span class="boolean-empty">—</span>
      </div>
      <div 
        class="long-text-column" 
        v-else 
        @mouseenter="!isTouchDevice() && showTooltip($event, { text: data[col.field] })"
          @mouseleave="!isTouchDevice() && hideTooltip()"
          @click="isTouchDevice() &&
          showTooltip($event, { alwaysShow: false, text: data[col.field], toggle: true })"
      >
        {{ data[col.field] }}
      </div>
    </template>

  </DataView>
  
  <CustomTooltip
    :text="tooltipText"
    :visible="tooltipVisible"
    :position="tooltipPosition"
  />

</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { DataView } from "@sttedra_ui/framework";
import CustomTooltip from '@/components/custom/CustomTooltip.vue';
import useTableConfig from "@/pages/_root/views/reports-details/data-source-type/table.view-config.js"; // eslint-disable-line

// #region Props, Emits
const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  columns: {
    type: Array,
    required: true,
  },
  rows: {
    type: Function,
    required: true,
  },
  onPageChange: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(['downloadRegistersEvent', 'pageChange']);
// #endregion

// #region Params, Ref, Reactives, Computed
const tableConfig = new useTableConfig(
  props.title,
  downloadRegisters,
  props.columns,
  props.rows
);

const tooltipText = ref('');
const tooltipVisible = ref(false);
const tooltipPosition = ref({ top: 0, left: 0 });

// #endregion

// #region Functions
function downloadRegisters() {
  emit('downloadRegistersEvent');
}

function handlePageChange(event) {
  emit('pageChange', event);
}

function isTouchDevice() {
  return window.matchMedia('(hover: none)').matches;
}

function isBooleanString(value) {
  return (
    value === 'True' ||
    value === 'False' ||
    value === 'true' ||
    value === 'false'
  );
}


function showTooltip(event, {
  alwaysShow = false,
  text = '',
  toggle = false
} = {}) {
  const el = event.currentTarget;
  const isOverflowing = el.scrollWidth > el.clientWidth;

  if (!isOverflowing && !alwaysShow) return;

  // Toggle solo en mobile
  if (toggle && tooltipVisible.value) {
    tooltipVisible.value = false;
    return;
  }

  tooltipText.value = text || el.textContent.trim();

  const rect = el.getBoundingClientRect();
  tooltipPosition.value = {
    top: rect.top - 8,
    left: rect.left + rect.width / 2,
  };

  tooltipVisible.value = isOverflowing || alwaysShow;
}

function hideTooltip() {
  tooltipVisible.value = false;
}

function handleOutsideTouch(event) {
  if (!tooltipVisible.value) return;

  const tooltipEl = document.querySelector('.custom-tooltip');
  if (!tooltipEl) return;

  if (!tooltipEl.contains(event.target)) {
    tooltipVisible.value = false;
  }
}

onMounted(() => {
  document.addEventListener('touchstart', handleOutsideTouch);
});


onBeforeUnmount(() => {
  document.removeEventListener('touchstart', handleOutsideTouch);
});


// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
.custom-table {
  font-variant-numeric: tabular-nums;
  :deep(.smart-content__title) {
    margin-top: 0;
    padding-left: 0;
  }

  :deep(.smart-content__center) {
    padding: 0;
  }

  :deep(.data-view__data table thead tr th) {
    color: black !important;
    text-align: left !important;
    padding: 1rem 1rem !important;
    border-width: 0 0 1px 0 !important;
    font-weight: 700 !important;
    font-size: 0.95rem !important;
    background: #f1f5f9 !important;
    border-bottom: 1px solid #e5e7eb !important;
  }

  :deep(.data-view__data table tbody tr td) {
    font-size: 0.90rem;
    vertical-align: middle;
  }
}

.boolean-empty {
  font-size: 0.9rem;
  color: #94a3b8;
  font-weight: 500;
}


.pi-check-circle,
.pi-times-circle {
  font-size: 0.9rem;
}

/* Transición del tooltip */
.fade-tooltip-enter-active,
.fade-tooltip-leave-active {
  transition: opacity 0.15s ease;
}

.fade-tooltip-enter-from,
.fade-tooltip-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .long-text-column {
    cursor: pointer;
  }

  .custom-tooltip {
    max-width: 90vw;
    font-size: 0.95rem;
  }
}

</style>
